<template>
  <v-menu :location="location" :offset="8" :transition="location === 'bottom' ? 'slide-y-transition' : 'slide-y-reverse-transition'">
    <template #activator="{ props, isActive }">
      <button
        type="button"
        class="lang-switch"
        :class="{ 'lang-switch--compact': compact, 'lang-switch--open': isActive }"
        v-bind="props"
        :aria-label="t('language.select')"
      >
        <span :class="['fi', `fi-${current.flag}`, 'lang-switch__flag']" />
        <span v-if="!compact" class="lang-switch__name">{{ current.nativeName }}</span>
        <ChevronDown class="lang-switch__chevron" :size="16" />
      </button>
    </template>

    <v-list class="lang-list" density="compact" nav>
      <v-list-item
        v-for="lang in languages"
        :key="lang.code"
        class="lang-list__item"
        :active="lang.code === current.code"
        @click="select(lang.code)"
      >
        <template #prepend>
          <span :class="['fi', `fi-${lang.flag}`, 'lang-list__flag']" />
        </template>
        <v-list-item-title class="lang-list__title">{{ lang.nativeName }}</v-list-item-title>
        <template #append>
          <Check v-if="lang.code === current.code" :size="16" class="lang-list__check" />
        </template>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Check, ChevronDown } from "lucide-vue-next";
import { SUPPORTED_LANGUAGES, getLanguageMeta } from "@/i18n/languages";
import { setLanguage } from "@/i18n";

defineProps({
  // Compact mode shows only the flag (used in tight footers / mobile).
  compact: { type: Boolean, default: false },
  // Menu open direction: "top" for footer, "bottom" for top nav bar.
  location: { type: String, default: "top" },
});

const { t, locale } = useI18n();

const languages = SUPPORTED_LANGUAGES;
const current = computed(() => getLanguageMeta(locale.value));

function select(code) {
  setLanguage(code);
}
</script>

<style scoped>
.lang-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(6px);
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--dark-color, #1f2937);
  line-height: 1;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.lang-switch:hover {
  border-color: var(--primary-color, #2563eb);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.lang-switch--open {
  border-color: var(--primary-color, #2563eb);
}

.lang-switch--compact {
  padding: 6px;
  gap: 4px;
}

.lang-switch__flag {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
  flex: 0 0 auto;
}

.lang-switch__name {
  white-space: nowrap;
}

.lang-switch__chevron {
  opacity: 0.55;
  transition: transform 0.2s ease;
}

.lang-switch--open .lang-switch__chevron {
  transform: rotate(180deg);
}

.lang-list {
  min-width: 200px;
  max-height: 60vh;
  border-radius: 14px !important;
  padding: 6px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.16) !important;
}

.lang-list__item {
  border-radius: 10px;
  min-height: 40px;
}

.lang-list__flag {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
  margin-right: 12px;
}

.lang-list__title {
  font-size: 0.88rem;
  font-weight: 500;
}

.lang-list__check {
  color: var(--primary-color, #2563eb);
}
</style>
